import { ChartBase } from 'components/common';
import { LabelItem } from 'components/segments';
import { Skeleton } from 'components/ui';
import { useMemo } from 'react';
import { useAppSelector } from 'store';
import { composableFiltersState } from 'store/composableFiltersSlice';
import { useGetCustomerStatisticsQuery } from 'store/services/segmentsApi';
import { userDetailsState } from 'store/userDetailsSlice';

export default function ChartOrderNumber() {
  const {
    output: { filtersApiPayload },
  } = useAppSelector(composableFiltersState);

  const { currentStore } = useAppSelector(userDetailsState);

  const { data, isFetching: loading } = useGetCustomerStatisticsQuery(
    {
      body: filtersApiPayload,
      params: {
        store_name: String(currentStore.completeInfo?.store_name),
        metric_list: ['order_frequency'],
      },
    },
    { skip: !!!currentStore.completeInfo?.store_name }
  );

  const totalSum = useMemo(() => {
    if (!data?.order_frequency?.length) return 1;
    return data.order_frequency.reduce((acc, curr) => acc + curr.value, 0);
  }, [data]);

  let loyals: number = 0;
  let repeats: number = 0;
  let oneTimers: number = 0;
  let signups: number = 0;

  data?.order_frequency?.forEach((item) => {
    switch (item.frequency) {
      case 'loyals':
        if (item.value) loyals = item.value;
        break;
      case 'repeats':
        if (item.value) repeats = item.value;
        break;
      case 'one_timers':
        if (item.value) oneTimers = item.value;
        break;
      case 'signups':
        if (item.value) signups = item.value;
        break;
      default:
        break;
    }
  });

  const chartData = useMemo(
    () => ({
      labels: ['Percentage'],
      datasets: [
        {
          label: 'Loyals',
          backgroundColor: '#125970',
          borderColor: '#125970',
          barThickness: 44,
          data: [loyals / totalSum],
        },
        {
          label: 'Repeats',
          backgroundColor: '#E2BE3B',
          borderColor: '#E2BE3B',
          barThickness: 44,
          data: [repeats / totalSum],
        },
        {
          label: 'One timers',
          backgroundColor: '#C28573',
          borderColor: '#C28573',
          barThickness: 44,
          data: [oneTimers / totalSum],
        },
        {
          label: 'Signups',
          backgroundColor: '#FF80E3',
          borderColor: '#FF80E3',
          barThickness: 44,
          data: [signups / totalSum],
        },
      ],
    }),
    [loyals, oneTimers, repeats, signups, totalSum]
  );

  const options = {
    indexAxis: 'y',
    maintainAspectRatio: false,
    scales: {
      y: {
        stacked: true,
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          display: false,
        },
      },
      x: {
        stacked: true,
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
  };
  if (!!!loading && !!!data?.order_frequency) return null;
  return (
    <div>
      <div className="flex justify-between -mb-8 ">
        <p className="font-bold">Orders distribution</p>
        <Skeleton isLoaded={!loading}>
          <p className="text-black text-opacity-30">
            Total customers: {totalSum.toLocaleString('en')}
          </p>
        </Skeleton>
      </div>

      <div className="-ml-2.5 relative">
        <ChartBase
          type="bar"
          // Passing empty array if loading to allow Skeleton to be shown
          data={loading ? [] : chartData}
          options={options}
        />
        {/* Positioning the Skeleton at same place absolutely, once the original chart size is bigger, note the negative margins */}
        <div className="absolute left-2.5 z-10 top-12 right-0.5">
          <Skeleton isLoaded={!loading}>
            <div className="h-11"></div>
          </Skeleton>
        </div>
      </div>
      <div className="flex -mt-10 space-x-24">
        <LabelItem
          color="graph_1"
          label="Loyals"
          highlightValue={(loyals / totalSum || '--').toLocaleString('en-US', {
            style: 'percent',
          })}
          referenceValue={loyals.toLocaleString('en') + ' customers'}
          loading={loading}
        />
        <LabelItem
          color="graph_2"
          label="Repeats"
          highlightValue={(repeats / totalSum || '--').toLocaleString('en-US', {
            style: 'percent',
          })}
          referenceValue={repeats.toLocaleString('en') + ' customers'}
          loading={loading}
        />
        <LabelItem
          color="graph_3"
          label="One timers"
          highlightValue={(oneTimers / totalSum || '--').toLocaleString(
            'en-US',
            {
              style: 'percent',
            }
          )}
          referenceValue={oneTimers.toLocaleString('en') + ' customers'}
          loading={loading}
        />
        <LabelItem
          color="graph_4"
          label="Signups"
          highlightValue={(signups / totalSum || '--').toLocaleString('en-US', {
            style: 'percent',
          })}
          referenceValue={signups.toLocaleString('en') + ' customers'}
          loading={loading}
        />
      </div>
    </div>
  );
}
